import { useRef, useState } from 'react';
import { LEAD_OPEN_STATUSES, LEAD_STATUS_LABELS, type LeadStatus, type LeadWithNames } from '../../shared/types';
import { melbourneToday } from '../../shared/dates';
import { api } from '../lib/api';
import { reportError } from '../lib/errorEvents';
import { canLockInLead, routeLeadStage } from '../lib/leadStage';
import { Sheet, SheetButton, SheetRow, SheetSection } from './Sheet';
import { DateSheet } from './DateSheet';
import { addDays, formatWeekdayDate } from '../lib/calendar';
import { DateField, Modal } from './primitives';

export const leadStageColors: Partial<Record<LeadStatus, string>> = {
  enquiry: 'var(--text-faint)', discovery_call: 'var(--accent-deep)', proposal_creation: 'var(--accent)',
  proposal_sent: 'var(--accent)', negotiation: 'var(--gold)', locked_in: 'var(--ok)', postponed: 'var(--text-faint)', won: 'var(--ok)', lost: 'var(--danger)', ghosted: 'var(--text-faint)',
};

/** Shared by the pipeline and detail. Date picking follows a successful
    postpone; Locked in picks its start date first and saves both at once. */
export function LeadStageSheet({ lead, onClose, onChanged, onConvert }: {
  lead: LeadWithNames; onClose: () => void; onChanged: () => void; onConvert: () => void;
}) {
  const [dateOpen, setDateOpen] = useState<'resurface' | 'start' | null>(null);
  const [busy, setBusy] = useState(false);
  const inFlight = useRef(false);
  const patch = async (fields: Record<string, unknown>) => {
    if (inFlight.current) return false;
    inFlight.current = true;
    setBusy(true);
    try {
      await api(`/api/admin/leads/${lead.id}`, { method: 'PATCH', json: fields });
      onChanged();
      return true;
    } catch (error) {
      reportError(error, 'Could not update the lead.');
      return false;
    } finally { inFlight.current = false; setBusy(false); }
  };
  const choose = async (status: LeadStatus) => {
    if (status === 'locked_in' && !canLockInLead(lead)) return;
    const action = routeLeadStage(status, melbourneToday(), { converted: lead.project_id !== null });
    if (action.kind === 'convert') { onClose(); onConvert(); return; }
    if (action.kind === 'lock') { setDateOpen('start'); return; }
    if (action.kind === 'postpone') {
      if (await patch({ status: 'postponed' })) setDateOpen('resurface');
    } else if (await patch(action.patch)) onClose();
  };
  const row = (status: LeadStatus) => <SheetRow key={status} active={lead.status === status} disabled={busy}
    icon={<span className="lead-stage-dot" style={{ background: leadStageColors[status] }} />}
    label={<span style={{ color: status === 'won' || status === 'locked_in' ? 'var(--ok)' : status === 'lost' ? 'var(--danger)' : undefined }}>{status === 'postponed' ? 'Not now' : LEAD_STATUS_LABELS[status]}</span>}
    sub={status === 'postponed' ? 'Postponed · set a resurface date' : status === 'locked_in' ? 'Confirmed · set a start date' : status === 'won' && lead.project_id === null ? 'Opens Convert to project' : undefined}
    right={lead.status === status ? '✓' : undefined} onSelect={() => void choose(status)} />;
  const closeDate = () => { if (!inFlight.current) onClose(); };
  if (dateOpen === 'start') return <DateSheet open title="Start date" context={lead.name} value={lead.resurface_date ?? addDays(melbourneToday(), 14)}
    onPick={date => { if (date) void patch({ status: 'locked_in', resurface_date: date }).then(ok => { if (ok) onClose(); }); }} onClose={closeDate} />;
  return dateOpen === 'resurface' ? <DateSheet open title="Resurface date" context={lead.name} value={lead.resurface_date} allowClear
    onPick={date => { void patch({ resurface_date: date }).then(ok => { if (ok) onClose(); }); }} onClose={closeDate} /> :
    <Sheet open variant="half" title="Move to stage" context={[lead.name, lead.company].filter(Boolean).join(' · ')}
      onClose={() => { if (!inFlight.current) onClose(); }} leftAction={<SheetButton disabled={busy} onClick={onClose}>Cancel</SheetButton>}>
      {LEAD_OPEN_STATUSES.map(row)}{canLockInLead(lead) && row('locked_in')}{row('postponed')}<SheetSection>Close</SheetSection>{(['won', 'lost', 'ghosted'] as const).map(row)}
    </Sheet>;
}

/** Desktop counterpart of the Start date sheet — dropping a card on the
    Locked in shelf opens this; nothing moves until a date is set. */
export function LeadStartDateModal({ lead, onClose, onLocked }: {
  lead: LeadWithNames; onClose: () => void; onLocked: (startDate: string) => void;
}) {
  const today = melbourneToday();
  const [date, setDate] = useState<string | null>(lead.resurface_date ?? addDays(today, 14));
  const [busy, setBusy] = useState(false);
  const chips = [
    { label: '+1 week', date: addDays(today, 7) },
    { label: '+2 weeks', date: addDays(today, 14) },
    { label: '+4 weeks', date: addDays(today, 28) },
    { label: '+3 months', date: addDays(today, 91) },
  ];
  const save = async () => {
    if (!date || busy) return;
    setBusy(true);
    try {
      await api(`/api/admin/leads/${lead.id}`, { method: 'PATCH', json: { status: 'locked_in', resurface_date: date } });
      onLocked(date);
    } catch (error) {
      reportError(error, 'Could not lock in the lead.');
      setBusy(false);
    }
  };
  return <Modal title="Start date" context={lead.name.toUpperCase()} width={440} mobile="half" onClose={onClose}
    hint="Convert to project nudges from this date" foot={<>
      <button className="btn" onClick={onClose}>Cancel</button>
      <button className="btn primary" disabled={!date || busy} onClick={() => void save()}>{date ? `Lock in · ${formatWeekdayDate(date)}` : 'Lock in'}</button>
    </>}>
    <div className="form-grid lead-start-modal">
      <div className="f-field span-2">
        <span className="f-label">Starts</span>
        <div className="chiprow-d">{chips.map(chip => <button key={chip.label} type="button" className={`chip${date === chip.date ? ' primary' : ''}`} onClick={() => setDate(chip.date)}>{chip.label}</button>)}</div>
        <DateField value={date} onChange={setDate} label="Start date" context={lead.name} />
        <span className="f-help">Locked in sits outside Pipeline and counts as won for conversion. Jobs won and revenue land on Convert.</span>
      </div>
    </div>
  </Modal>;
}
